// @ts-check

/**
 * Animated details element.
 * Requires a transition on element following summary to listen for transitionend
 */
class AnimatedDetails extends HTMLElement {
  constructor() {
    super()
    this.setup()
  }

  setup() {
    this.detailsEl = this.querySelector('details')
    this.summary = this.querySelector('summary')
    this.toggledEl = this.summary.nextElementSibling

    this.openState = 'open'
    this.closedState = 'closed'
    this.openingState = 'opening'
    this.closingState = 'closing'

    this.classes = {
      open: 'open',
      closed: 'closed',
      beforeClosing: 'before-closing',
      beforeOpening: 'before-opening',
      closing: 'closing',
      opening: 'opening'
    }

    this.state = this.detailsEl.hasAttribute('open') ? this.openState : this.closedState

    this.detailsEl.classList.add(this.classes[this.state])
    // ensures doesn't close if clicked inside
    this.toggledEl.tabIndex = -1

    /**
     * when a transition has ended, if this property was what ended then
     * the details is considered "closed" or "open"
     */
    this.transitionedPropertyName = 'transform'

    this.bindListeners()
  }

  bindListeners() {
    this.detailsEl.addEventListener('toggle', this.onToggle.bind(this))
    this.summary.addEventListener('click', this.onClick.bind(this))
    this.detailsEl.addEventListener('focusout', this.onFocusOut.bind(this))
    this.toggledEl.addEventListener('transitionend', this.onTransitionEnd.bind(this))
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close()
      }
      /**
       * slight delay seems to resolve issues with click
       * not registering when it also triggers a focusout event on FF.
       */
    }, 100)
  }

  onTransitionEnd(e) {
    e.stopPropagation()
    if (
      e.propertyName === this.transitionedPropertyName &&
      e.target === this.toggledEl
      ) {
      if (this.state === this.closingState) {
        this.cancelOpening()
        this.cancelClosing()
        this.detailsEl.classList.add(this.classes.closed)
        this.detailsEl.removeAttribute('open')
        this.state = this.closedState
      } else {
        this.cancelClosing()
        this.cancelOpening()
        this.detailsEl.classList.add(this.classes.open)
        this.state = this.openState
      }
    }
  }

  cancelOpening() {
    this.detailsEl.classList.remove(this.classes.opening)
    this.detailsEl.classList.remove(this.classes.beforeOpening)
    this.detailsEl.classList.remove(this.classes.open)
  }

  cancelClosing() {
    this.detailsEl.classList.remove(this.classes.closing)
    this.detailsEl.classList.remove(this.classes.beforeClosing)
    this.detailsEl.classList.remove(this.classes.closed)
  }

  onClick(e) {
    e.preventDefault()
    if (this.state === this.openState || this.state == this.openingState) {
      this.close()
    } else {
      this.open()
    }
  }

  close() {
    if (
      this.state !== this.closedState &&
      this.state !== this.closingState
    ) {
      this.state = this.closingState
      this.detailsEl.style.setProperty(
        "--summary-height",
        `${this.summary.getBoundingClientRect().height}px`
      )
      requestAnimationFrame(() => {
        this.cancelOpening()
        this.detailsEl.classList.add(this.classes.beforeClosing)
        requestAnimationFrame(() => {
          this.detailsEl.classList.add(this.classes.closing)
        })
      })
    }
  }

  open() {
    if (
      this.state !== this.openingState &&
      this.state !== this.openState
    ) {
      const wasClosing = this.state === this.closingState
      this.state = this.openingState
      this.detailsEl.style.setProperty(
        "--summary-height",
        `${this.summary.getBoundingClientRect().height}px`
      )
      this.detailsEl.setAttribute('open', 'true')
      if (wasClosing) {
        this.cancelClosing()
        this.detailsEl.classList.add(this.classes.opening)
      } else {
        requestAnimationFrame(() => {
          this.cancelClosing()
          this.detailsEl.classList.add(this.classes.beforeOpening)
          requestAnimationFrame(() => {
            this.detailsEl.classList.add(this.classes.opening)
          })
        })
      }
    }
  }

  onToggle() {
    if (this.detailsEl.hasAttribute('open')) {
      this.summary.setAttribute('aria-expanded', 'true')
    } else {
      this.summary.setAttribute('aria-expanded', 'false')
    }
  }
}

customElements.define('animated-details', AnimatedDetails)

/**
 * Mobile drawer
 * Adds scroll lock class to body and closes child details when closing.
 * Sets CSS variable for header bottom (accounts for announcement bar). used to set drawer height.
 */
class HeaderDropdown extends AnimatedDetails {
  setup() {
    this.addEventListener('toggle', this.onMenuItemToggle.bind(this))
    this.addEventListener('predictive-search:toggle', this.onSearchToggle.bind(this))
    super.setup()
  }

  onSearchToggle({ detail: { open }}) {
    removeTrapFocus()
    Array.from(this.querySelectorAll('menu-item-dropdown'))
      .forEach(menuItemDropdown => {
        menuItemDropdown.setChildElementsFocusable(false, open)
      })
    Array.from(this.querySelectorAll('mobile-predictive-search ~ li a')).forEach(topLevelLink => {
      if (open) {
        topLevelLink.tabIndex = '-1'
      } else {
        topLevelLink.removeAttribute('tabindex')
      }
    })
    setTimeout(() => {
      trapFocus(this, document.activeElement)
    })
  }

  open() {
    const height = this.parentElement.getBoundingClientRect().bottom
    document.documentElement.style.setProperty('--header-bottom-position', `${height}px`)
    document.body.classList.add(`overflow-hidden-tablet`)

    super.open()
    setTimeout(() => {
      trapFocus(this, this.summary)
    })
  }

  close() {
    removeTrapFocus()
    super.close()
    document.body.classList.remove('overflow-hidden-tablet')
    const childDetails = Array.from(this.querySelectorAll('menu-item-dropdown'))

    setTimeout(() => {
      childDetails.forEach(childDetail => childDetail.close())
    }, 0)
  }

  onMenuItemToggle() {
    removeTrapFocus()
    trapFocus(this, document.activeElement)
  }

}

customElements.define('header-dropdown', HeaderDropdown)

/**
 * Mobile menu details element.
 * Adds height transition on <details> element.
 * Also disables focus on child elements when closed to permit proper usage of focus trap.
 * without that, the "last element" could be the child of a closed details and couldn't be focused.
 */
class MenuItemDropdown extends AnimatedDetails {
  setup() {
    super.setup()

    this.setChildElementsFocusable(false)
  }

  /**
   * When disabling focus, all focusable elements get tabindex="-1" except the summary.
   * When enabling, tabindex is removed from all focusable elements.
   *
   * @param {boolean} focusable
   */
  setChildElementsFocusable(focusable = true, hideSummary = false) {
    if (focusable) {
      Array.from(this.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
      )).forEach(focusableEl => focusableEl.removeAttribute('tabindex'))
    } else {
      Array.from(this.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
      )).forEach(focusableEl => focusableEl.tabIndex = '-1')
      if (!hideSummary) this.summary.removeAttribute('tabindex')
    }
  }

  open() {
    this.detailsEl.setAttribute('open', 'true')
    const finalHeight = this.detailsEl.getBoundingClientRect().height
    this.detailsEl.removeAttribute('open')
    this.detailsEl.style.setProperty('--final-height', `${finalHeight}px`)

    this.setChildElementsFocusable(true)

    super.open()
  }

  close() {
    super.close()
    this.setChildElementsFocusable(false)
  }
}

customElements.define('menu-item-dropdown', MenuItemDropdown)
